<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import Icon from '@/components/Icon.vue'
import MdView from '@/components/MdView.vue'
import MemoryAttachment from '@/components/memory/MemoryAttachment.vue'
import { useUi } from '@/stores/ui'
import { kindColor } from '@/stores/memory'
import { memoryApi, type MemoryNodeDetail } from '@/lib/memoryApi'

/* Quick-reader for a knowledge-graph node. Opens over whatever you're doing
   (a chat message, a ticket) when you click a [text](node-slug) link — read it
   without leaving the page. It fetches its own detail, so it never touches the
   full memory page's selection. Clicking a connection or an in-content knowledge
   link opens it here too, with a back button; "Open full" hands off to the
   memory page when you want the graph. */
const ui = useUi()
const router = useRouter()

// In-modal browsing history (slugs). The store just supplies the entry node;
// stepping between linked nodes stays local so the memory page is undisturbed.
const history = ref<string[]>([])
const detail = ref<MemoryNodeDetail | null>(null)
const loading = ref(false)
const failed = ref(false)
let seq = 0

const open = computed(() => ui.knowledgeNode !== null)
const currentId = computed(() => history.value[history.value.length - 1] ?? null)
const canBack = computed(() => history.value.length > 1)

// A fresh open (new node, or the same node re-opened — the nonce changes) reseeds
// the history; closing clears it.
watch(
  () => ui.knowledgeNode,
  (kn) => {
    if (!kn) { history.value = []; detail.value = null; return }
    history.value = [kn.id]
  },
  { immediate: true },
)
watch(currentId, (id) => { if (id) void load(id); else detail.value = null }, { immediate: true })

async function load(id: string) {
  const mine = ++seq
  loading.value = true
  failed.value = false
  detail.value = null
  try {
    const d = await memoryApi.node(id)
    if (mine !== seq) return // a newer navigation superseded this fetch
    detail.value = d
  } catch {
    if (mine === seq) failed.value = true
  } finally {
    if (mine === seq) loading.value = false
  }
}

function goTo(id: string) {
  if (id && id !== currentId.value) history.value.push(id)
}
function back() { if (canBack.value) history.value.pop() }
function close() { ui.closeKnowledge() }
function openFull() {
  const id = currentId.value
  ui.closeKnowledge()
  if (id) void router.push({ name: 'memory', query: { node: id } })
}

function fmtDate(s: string): string {
  const d = new Date(s)
  return (
    d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) +
    ' ' +
    d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  )
}

function onKey(e: KeyboardEvent) {
  if (open.value && e.key === 'Escape') { e.preventDefault(); close() }
}
onMounted(() => document.addEventListener('keydown', onKey))
onBeforeUnmount(() => document.removeEventListener('keydown', onKey))
</script>

<template>
  <div v-if="open" class="modal-back in" @mousedown.self="close">
    <div class="modal modal--knowledge" role="dialog" aria-modal="true">
      <div class="kv__bar">
        <button v-if="canBack" class="icon-mini" data-tip="Back" @click="back"><Icon name="chevL" :size="16" /></button>
        <span class="kv__bar-label"><Icon name="memory" :size="14" /> Knowledge</span>
        <div class="grow" />
        <button class="btn btn--ghost btn--sm" data-tip="Open in the knowledge graph" @click="openFull">Open full</button>
        <button class="icon-mini" data-tip="Close" @click="close"><Icon name="x" :size="16" /></button>
      </div>

      <div class="mem__node kv__body">
        <div v-if="loading && !detail" class="mem__node-loading">Opening…</div>
        <div v-else-if="failed" class="mem__node-loading">Couldn’t open this node — it may have been removed.</div>
        <template v-else-if="detail">
          <header class="mem__node-head">
            <span class="mem__node-kind" :style="{ color: kindColor(detail.node.kind) }">{{ detail.node.kind }}</span>
            <h2 class="mem__node-title">{{ detail.node.title }}</h2>
            <p v-if="detail.node.summary" class="mem__node-summary">{{ detail.node.summary }}</p>
          </header>
          <div class="mem__node-content">
            <MdView :source="detail.node.content || '_No details yet._'" @node-link="goTo" />
          </div>
          <section v-if="detail.files?.length" class="mem__node-section">
            <h4>Attachments</h4>
            <div class="mem__attachments">
              <MemoryAttachment v-for="f in detail.files" :key="f.id" :file="f" />
            </div>
          </section>
          <section v-if="detail.edges.length" class="mem__node-section">
            <h4>Connections</h4>
            <button
              v-for="e in detail.edges"
              :key="e.src + e.dst + e.rel"
              class="mem__edge-row"
              @click="goTo(e.peer.id)"
            >
              <span class="mem__edge-rel">{{ e.out ? e.rel.replaceAll('_', ' ') : '← ' + e.rel.replaceAll('_', ' ') }}</span>
              <span class="mem__chip-dot" :style="{ background: kindColor(e.peer.kind) }" />
              <span class="mem__edge-peer">{{ e.peer.title }}</span>
            </button>
          </section>
          <section v-if="detail.sources.length" class="mem__node-section">
            <h4>Remembered from</h4>
            <blockquote v-for="s in detail.sources" :key="s.id" class="mem__source">
              <p>“{{ s.raw.length > 220 ? s.raw.slice(0, 217) + '…' : s.raw }}”</p>
              <footer>{{ s.by_user }} · {{ fmtDate(s.created_at) }}</footer>
            </blockquote>
          </section>
        </template>
      </div>
    </div>
  </div>
</template>
